import { ArrowTopRightOnSquareIcon } from "@heroicons/react/24/outline";
import { Card, RowAction, SectionTitle } from "../ui";
import { countryName, formatKigaliDate, useEducationLevels, useFieldsOfStudy } from "../scholarships/model";
import { sectorLabel } from "../scholarships/review/model";
import { AMOUNTS, formatMoney } from "../scholarships/funding";
import EligibilityNote from "../scholarships/student/EligibilityNote";

// One label and value per row, two columns from small screens up
function Facts({ rows }) {
  return (
    <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
      {rows.map(([label, value]) => (
        <div key={label}>
          <dt className="text-slate-500">{label}</dt>
          <dd className="font-medium text-slate-800">{value || "—"}</dd>
        </div>
      ))}
    </dl>
  );
}

// The student's studies: from their academic profile while a draft, then as they were sent
export function Studies({ draft, profile, background, levels, fields }) {
  const nameIn = (list, id, key) => list.find((x) => x.id === id)?.[key] ?? "—";
  const studies = draft
    ? profile && {
        level: profile.currentLevel?.name,
        field: profile.fieldOfStudy?.name,
        institution: profile.institutionName,
        programme: profile.programmeName,
        grade: profile.gradePercent,
        year: profile.completionYear,
      }
    : background?.currentLevelId && {
        level: nameIn(levels, background.currentLevelId, "levelName"),
        field: nameIn(fields, background.fieldOfStudyId, "fieldName"),
        institution: background.previousInstitution,
        programme: background.previousProgramme,
        grade: background.gradePercent,
        year: background.completionYear,
      };
  if (!studies) return draft ? <EligibilityNote eligibility={{ profileRequired: true }} /> : <p className="text-sm text-slate-500">No studies were sent with this application.</p>;
  const rows = [
    ["Current level of study", studies.level],
    ["Field of study", studies.field],
    ["School or university", studies.institution],
    ["Programme or subject combination", studies.programme],
    ["Final grade", studies.grade == null ? "—" : `${Number(studies.grade)}%`],
    ["Year completed", studies.year],
  ];
  return <Facts rows={rows} />;
}

// A file with an Open action
function FileRow({ name, hint, onOpen }) {
  return (
    <li className="flex items-center justify-between gap-3 py-3 text-sm">
      <span className="min-w-0">
        <span className="block truncate text-slate-800">{name}</span>
        {hint && <span className="block truncate text-xs text-slate-500">{hint}</span>}
      </span>
      <RowAction Icon={ArrowTopRightOnSquareIcon} label="Open" variant="ghost" onClick={onOpen} />
    </li>
  );
}

/**
 * A submitted abroad loan application as staff read it: studies, where the student was admitted,
 * employment, the loan asked for and the documents. `requirements` name the documents (from the
 * call's programme); `onOpen` and `onOpenLegacy` load a file.
 */
export function ApplicationDetails({ application, requirements = [], onOpen, onOpenLegacy }) {
  const { data: levels = [] } = useEducationLevels();
  const { data: fields = [] } = useFieldsOfStudy();
  const { admission, loan } = application;
  // Without the call's requirements (the evaluator's view) the code reads as words: ADMISSION_LETTER is "Admission letter"
  const readable = (code = "") => code.charAt(0) + code.slice(1).toLowerCase().replaceAll("_", " ");
  const requirementOf = (d) => requirements.find((r) => r.id === d.requirementId)?.label ?? readable(d.requirementCode);
  const employment = application.employed == null ? null : application.employed ? ["Yes", application.employer].filter(Boolean).join(", at ") : "No";

  return (
    <div className="flex flex-col gap-4">
      <Card title="Studies so far">
        <Studies background={application.background} levels={levels} fields={fields} />
      </Card>
      <Card title="Studies abroad">
        <Facts
          rows={[
            ["University", admission?.institution],
            ["Programme", admission?.programme],
            ["Country", admission?.countryCode && countryName(admission.countryCode)],
            ["Priority sector", application.courseSector && sectorLabel(application.courseSector)],
            ["Studies start", loan?.studyStartDate && formatKigaliDate(loan.studyStartDate)],
            ["Studies end", loan?.studyEndDate && formatKigaliDate(loan.studyEndDate)],
            ["Employed", employment],
          ]}
        />
      </Card>
      <Card title="Loan asked for">
        <Facts rows={AMOUNTS.map(({ key, label }) => [label, loan?.[key] == null ? null : formatMoney(loan[key], loan.currency)])} />
      </Card>
      <Card title="Documents">
        {application.documents.length === 0 && !application.legacyDocuments?.length && <p className="text-sm text-slate-500">No documents.</p>}
        <ul className="flex flex-col divide-y divide-slate-100">
          {application.documents.map((d) => (
            <FileRow key={d.id} name={requirementOf(d)} hint={d.fileName} onOpen={() => onOpen(d)} />
          ))}
        </ul>
        {application.legacyDocuments?.length > 0 && (
          <div className="mt-4">
            <SectionTitle title="From the former system" />
            <ul className="mt-2 flex flex-col divide-y divide-slate-100">
              {application.legacyDocuments.map((d) => (
                <FileRow key={d.id} name={d.fileName} onOpen={() => onOpenLegacy(d)} />
              ))}
            </ul>
          </div>
        )}
      </Card>
    </div>
  );
}
